import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'

import { queryClient } from './utils/queryClient'
// import {
//   ConnectionProvider,
//   WalletProvider,
// } from "@solana/wallet-adapter-react";
// import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
// import { PhantomWalletAdapter } from "@solana/wallet-adapter-wallets";

import { AuthProvider } from './contexts/AuthProvider'
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext'
import { WagmiProvider } from 'wagmi'
import { RainbowKitProvider } from '@rainbow-me/rainbowkit'
import { rainbowKitConfig } from './utils/currencies'
import { ChainContextProvider } from './contexts/SolanaChainContextProvider'
import { SelectedWalletAccountContextProvider } from './contexts/WalletAccountProvider'
import { RpcContextProvider } from './contexts/SolanaRpcContext'
import { PhantomSolanaMobileProvider } from './contexts/PhantomSolanaMobileContext'

import GlobalStyle from './global-styles'
import App from './App'

import { configure } from '@paytrie/client'
import { usePageStateStore } from 'src/reducers'

configure({
  baseUrl: import.meta.env.VITE_PAYTRIE_API_URL,
  headers: () => {
    const { authToken } = usePageStateStore.getState()
    return authToken ? { Authorization: `Bearer ${authToken}` } : undefined
  },
})

import './utils/i18n'
import './input.css'
import '@rainbow-me/rainbowkit/styles.css'

// Initialize Sentry service
if (import.meta.env.MODE === 'production') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: 'production', // Explicitly set to production
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [__SENTRY_APPLICATION_KEY__],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
    // Configure which URLs to send trace data to
    tracePropagationTargets: [
      /^https:\/\/paytrie\.com/,
      /^https:\/\/app\.paytrie\.com/,
      /^\//, // Include relative URLs within your own domain
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Add custom error processing for wallet errors
    beforeSend(event) {
      // Detect wallet-related errors
      const errorMessage = event.exception?.values?.[0]?.value || ''
      const isWalletError =
        errorMessage.includes('provider') ||
        errorMessage.includes('disconnect') ||
        errorMessage.includes('wallet')

      if (isWalletError) {
        // Add a custom tag for easier filtering
        event.tags = event.tags || {}
        event.tags.error_source = 'wallet'

        // Improve error grouping with custom fingerprint
        event.fingerprint = [
          'wallet-connection-error',
          errorMessage.includes('disconnect') ? 'disconnect' : 'other',
        ]

        // Add package versions as context
        event.contexts = event.contexts || {}
        event.contexts.versions = {
          rainbowKit: '2.2.3',
          wagmi: '2.14.11',
          viem: '2.23.2',
        }
      }

      return event
    },
  })
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

const app = (
  <WagmiProvider config={rainbowKitConfig}>
    <QueryClientProvider client={queryClient}>
      <ChainContextProvider>
        <SelectedWalletAccountContextProvider>
          <PhantomSolanaMobileProvider>
            <RpcContextProvider>
              <RainbowKitProvider>
                <AuthProvider>
                  <GlobalStyle />
                  <FeatureFlagsProvider>
                    <App />
                  </FeatureFlagsProvider>
                </AuthProvider>
              </RainbowKitProvider>
            </RpcContextProvider>
          </PhantomSolanaMobileProvider>
        </SelectedWalletAccountContextProvider>
      </ChainContextProvider>
    </QueryClientProvider>
  </WagmiProvider>
)

/**
 * Mock Service Worker intercepts every request the app makes, so it is opt-in:
 * local development talks to the real API unless VITE_ENABLE_MSW is set.
 */
const isMockServiceWorkerEnabled =
  import.meta.env.MODE === 'development' &&
  import.meta.env.VITE_ENABLE_MSW === 'true'

if (isMockServiceWorkerEnabled) {
  import('./mocks/browser')
    .then(({ worker }) => worker.start({ onUnhandledRequest: 'bypass' }))
    .then(() => root.render(app))
} else {
  root.render(app)
}
